import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

interface PlanFeature {
  id: string;
  feature_text: string;
  display_order: number;
}

interface PricingPlan {
  id: string;
  name: string;
  slug: string;
  price_display: string;
  is_featured: boolean;
  display_order: number;
  cta_text: string;
  stripe_price_id: string | null;
  features: PlanFeature[];
}

const FALLBACK: PricingPlan[] = [
  {
    id: 'free',
    name: 'Inicial',
    slug: 'free',
    price_display: 'Grátis',
    is_featured: false,
    display_order: 1,
    cta_text: 'Começar grátis',
    stripe_price_id: null,
    features: [
      { id: 'f1', feature_text: 'Mapa natal completo', display_order: 1 },
      { id: 'f2', feature_text: 'Previsão diária', display_order: 2 },
      { id: 'f3', feature_text: '3 perguntas por mês', display_order: 3 },
      { id: 'f4', feature_text: 'Tarô básico', display_order: 4 },
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    slug: 'premium',
    price_display: 'R$ 24,90/mês',
    is_featured: true,
    display_order: 2,
    cta_text: 'Começar minha leitura',
    stripe_price_id: null,
    features: [
      { id: 'f5', feature_text: 'Tudo do Inicial', display_order: 1 },
      { id: 'f6', feature_text: 'Sinastria ilimitada', display_order: 2 },
      { id: 'f7', feature_text: 'I Ching + Numerologia', display_order: 3 },
      { id: 'f8', feature_text: 'Exportação PDF', display_order: 4 },
      { id: 'f9', feature_text: 'Previsão semanal e mensal', display_order: 5 },
      { id: 'f10', feature_text: 'Histórico completo', display_order: 6 },
    ],
  },
  {
    id: 'annual',
    name: 'Anual',
    slug: 'annual',
    price_display: 'R$ 199,90/ano',
    is_featured: false,
    display_order: 3,
    cta_text: 'Começar com desconto',
    stripe_price_id: null,
    features: [
      { id: 'f11', feature_text: 'Equivale a R$ 16,66/mês', display_order: 1 },
      { id: 'f12', feature_text: 'Economize R$ 99/ano vs mensal', display_order: 2 },
      { id: 'f13', feature_text: 'Tudo do Premium', display_order: 3 },
      { id: 'f14', feature_text: 'Astrocartografia', display_order: 4 },
      { id: 'f15', feature_text: 'Quiromancia', display_order: 5 },
      { id: 'f16', feature_text: 'Acesso antecipado a novidades', display_order: 6 },
    ],
  },
];

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" style={{ flexShrink: 0, marginTop: '3px' }}>
      <path d="M2 7.5l3.5 3.5 6.5-7" stroke="#D4AF37" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function DynamicPricingSection() {
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const { data: plansData, error } = await supabase
          .from('pricing_plans')
          .select('*')
          .eq('active', true)
          .order('display_order');

        if (error || !plansData?.length) throw new Error('no data');

        const withFeatures = await Promise.all(
          plansData.map(async (plan) => {
            const { data: featuresData } = await supabase
              .from('plan_features')
              .select('id, feature_text, display_order')
              .eq('plan_id', plan.id)
              .eq('active', true)
              .order('display_order');
            return { ...plan, features: featuresData ?? [] };
          })
        );
        setPlans(withFeatures);
      } catch {
        setPlans(FALLBACK);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <section id="pricing" style={{ padding: 'clamp(4rem, 8vw, 7rem) 2rem', borderTop: '1px solid var(--line-soft)' }}>
        <div style={{ maxWidth: '1060px', margin: '0 auto', textAlign: 'center' }}>
          <span className="eyebrow">Carregando planos…</span>
        </div>
      </section>
    );
  }

  return (
    <section
      id="pricing"
      style={{
        padding: 'clamp(4rem, 8vw, 7rem) 2rem',
background: 'radial-gradient(ellipse 100% 80% at 50% 100%, #1A0B2E 0%, #0A0416 60%, #0A0416 100%)',        borderTop: '1px solid var(--line-soft)',
      }}
    >
      <div style={{ maxWidth: '1060px', margin: '0 auto' }}>
        <p className="eyebrow" style={{ textAlign: 'center', marginBottom: '1rem', color: '#D4AF37' }}>
          Assinatura
        </p>
        <h2 style={{ textAlign: 'center', margin: '0 0 3.5rem', color: '#ffffff' }}>
          Escolha a sua leitura.
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '1.5rem',
        }} className="dyn-pricing-grid">
          {plans.map((plan) => (
            <div
              key={plan.id}
              style={{
                position: 'relative',
                background: plan.is_featured
                  ? 'linear-gradient(135deg, #2D1B69 0%, #1A0B2E 100%)'
                  : 'rgba(26,11,46,0.7)',
                border: plan.is_featured
                  ? '2px solid rgba(212,175,55,0.8)'
                  : '1px solid rgba(212,175,55,0.2)',
                borderRadius: '16px',
                padding: '2rem 1.75rem',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: plan.is_featured
                  ? '0 0 40px rgba(212,175,55,0.25)'
                  : 'none',
                transform: plan.is_featured ? 'scale(1.03)' : 'scale(1)',
                transition: 'box-shadow 0.3s ease, transform 0.3s ease',
              }}
              onMouseEnter={e => {
                const el = e.currentTarget as HTMLElement;
                el.style.boxShadow = '0 0 36px rgba(212,175,55,0.35)';
                el.style.transform = plan.is_featured ? 'scale(1.05)' : 'scale(1.02)';
              }}
              onMouseLeave={e => {
                const el = e.currentTarget as HTMLElement;
                el.style.boxShadow = plan.is_featured ? '0 0 40px rgba(212,175,55,0.25)' : 'none';
                el.style.transform = plan.is_featured ? 'scale(1.03)' : 'scale(1)';
              }}
            >
              {plan.is_featured && (
                <div style={{
                  position: 'absolute', top: '-14px', left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'linear-gradient(135deg, #D4AF37, #F4E4C1)',
                  color: '#0A0416',
                  fontSize: '10px', fontWeight: 800,
                  letterSpacing: '0.18em', textTransform: 'uppercase',
                  padding: '0.3rem 1rem', borderRadius: '100px',
                  fontFamily: 'var(--font-body)',
                  whiteSpace: 'nowrap',
                }}>
                  Mais Popular
                </div>
              )}

              <h3 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--text-22)', fontStyle: 'italic', fontWeight: 400,
                color: '#ffffff', margin: '0 0 0.75rem',
              }}>{plan.name}</h3>

              <p style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: 400,
                color: '#D4AF37', margin: '0 0 1.5rem', lineHeight: 1,
              }}>{plan.price_display}</p>

              <div style={{ borderTop: '1px solid rgba(212,175,55,0.2)', marginBottom: '1.25rem' }} />

              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 auto', display: 'flex', flexDirection: 'column', gap: '0.65rem', flex: 1 }}>
                {plan.features.map((f) => (
                  <li key={f.id} style={{ display: 'flex', gap: '0.55rem', alignItems: 'flex-start' }}>
                    <CheckIcon />
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-13)', color: 'rgba(255,255,255,0.78)', lineHeight: 1.5 }}>
                      {f.feature_text}
                    </span>
                  </li>
                ))}
              </ul>

              <div style={{ marginTop: '2rem' }}>
                <Link
                  to={plan.slug === 'free' ? '/register' : `/register?plan=${plan.slug}`}
                  style={{
                    display: 'block', textAlign: 'center',
                    padding: '0.85rem 1.5rem',
                    background: plan.is_featured
                      ? 'linear-gradient(135deg, #D4AF37 0%, #F4E4C1 50%, #D4AF37 100%)'
                      : 'transparent',
                    border: plan.is_featured ? 'none' : '1px solid rgba(212,175,55,0.5)',
                    color: plan.is_featured ? '#0A0416' : '#D4AF37',
                    fontFamily: 'var(--font-body)', fontSize: 'var(--text-13)',
                    fontWeight: 700, textDecoration: 'none',
                    borderRadius: '8px', letterSpacing: '0.04em',
                    transition: 'box-shadow 0.25s ease, opacity 0.2s ease',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 0 20px rgba(212,175,55,0.4)';
                    (e.currentTarget as HTMLAnchorElement).style.opacity = '0.9';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
                    (e.currentTarget as HTMLAnchorElement).style.opacity = '1';
                  }}
                >
                  {plan.cta_text}
                </Link>
              </div>
            </div>
          ))}
        </div>

        <p style={{ textAlign: 'center', fontFamily: 'var(--font-body)', fontSize: 'var(--text-13)', color: 'rgba(255,255,255,0.4)', marginTop: '2rem' }}>
          Cancele quando quiser · Sem fidelidade
        </p>
      </div>

      <style>{`
        @media (max-width: 767px) {
          .dyn-pricing-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
}
